import { useEffect, useState } from "react";
import { CheckCircle2, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { APPT_STATUS, APPT_TYPES } from "@/lib/format";
import { Btn, Chip, Field, Modal, Pill } from "@/components/rp";
import { AddressInput } from "@/components/AddressInput";
import { Actions, useSubmit } from "@/components/admin/dialogs";

const toLocal = (iso) => { const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const KIND = { staff: "Staff", impresa: "Impresa", esterno: "Esterno" };

const InviteesPicker = ({ value, onChange }) => {
  const [opts, setOpts] = useState([]);
  const [ext, setExt] = useState({ name: "", email: "" });
  useEffect(() => { api.get("/admin/appuntamenti/invitabili").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  const add = (p) => { if (p && !value.some((x) => x.email === p.email)) onChange([...value, p]); };
  const addExt = () => {
    if (!/^\S+@\S+\.\S+$/.test(ext.email.trim())) { toast.error("Email non valida"); return; }
    add({ name: ext.name.trim() || ext.email.trim(), email: ext.email.trim().toLowerCase(), kind: "esterno" });
    setExt({ name: "", email: "" });
  };
  return (
    <div className="rounded-2xl bg-[#F7F7F7] border border-[#EBEBEB] p-4 space-y-3" data-testid="appointment-invitees">
      <p className="rp-label">Invitati oltre al cliente <span className="normal-case tracking-normal font-normal text-[#757575]">ricevono email con file calendario, modifiche e annullamento</span></p>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((p) => (
            <span key={p.email} className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EBEBEB] pl-3 pr-1 py-1 text-sm" data-testid={`invitee-${p.email}`}>
              {p.name} <span className="text-xs text-[#757575]">{KIND[p.kind]}</span>
              <button type="button" onClick={() => onChange(value.filter((x) => x.email !== p.email))} className="h-6 w-6 rounded-full hover:bg-[#EBEBEB] flex items-center justify-center" aria-label="Rimuovi" data-testid={`invitee-remove-${p.email}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <select value="" onChange={(e) => add(opts.find((o) => o.email === e.target.value))} className="rp-field bg-white" data-testid="invitee-select">
        <option value="">Aggiungi staff RP Home o impresa…</option>
        {["staff", "impresa"].map((k) => (
          <optgroup key={k} label={k === "staff" ? "Staff RP Home" : "Imprese e subappaltatori"}>
            {opts.filter((o) => o.kind === k && !value.some((x) => x.email === o.email)).map((o) => <option key={o.email} value={o.email}>{o.name} · {o.email}</option>)}
          </optgroup>
        ))}
      </select>
      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
        <input value={ext.name} onChange={(e) => setExt({ ...ext, name: e.target.value })} className="rp-field bg-white" placeholder="Nome (esterno)" data-testid="invitee-ext-name" />
        <input value={ext.email} onChange={(e) => setExt({ ...ext, email: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExt(); } }} className="rp-field bg-white" placeholder="Email" data-testid="invitee-ext-email" />
        <Btn type="button" variant="outline" onClick={addExt} data-testid="invitee-ext-add"><UserPlus className="h-4 w-4" /> Invita</Btn>
      </div>
    </div>
  );
};

const EditActions = ({ a, onDone }) => {
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  const status = (s) => run(() => api.patch(`/appointments/${a.id}`, { status: s }), `Appuntamento ${APPT_STATUS[s].toLowerCase()}`);
  const del = () => window.confirm("Eliminare l'appuntamento? Cliente e invitati ricevono l'annullamento.") && run(() => api.delete(`/appointments/${a.id}`), "Appuntamento eliminato");
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-[#EBEBEB] pt-4" data-testid="appointment-edit-actions">
      <Pill>{APPT_STATUS[a.status]}</Pill>
      <span className="flex-1" />
      {a.status === "richiesto" && <Btn type="button" className="h-9" onClick={() => status("confermato")} data-testid="appointment-dialog-confirm"><CheckCircle2 className="h-4 w-4" /> Conferma</Btn>}
      {["richiesto", "confermato"].includes(a.status) && <Btn type="button" variant="secondary" className="h-9" onClick={() => status("completato")} data-testid="appointment-dialog-complete">Completato</Btn>}
      {["richiesto", "confermato"].includes(a.status) && <Btn type="button" variant="outline" className="h-9" onClick={() => status("annullato")} data-testid="appointment-dialog-cancel">Annulla appuntamento</Btn>}
      <Btn type="button" variant="danger" className="h-9" onClick={del} data-testid="appointment-dialog-delete"><Trash2 className="h-4 w-4" /> Elimina</Btn>
    </div>
  );
};

export const AppointmentDialog = ({ open, onClose, clientId, onSaved, appt, initialDate }) => {
  const empty = { client_id: clientId || "", title: "", type: "sopralluogo", date: initialDate || "", duration_min: 60, location: "", notes: "", invitees: [] };
  const [f, setF] = useState(empty);
  const [clients, setClients] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF(appt ? { ...empty, ...appt, date: toLocal(appt.date), invitees: appt.invitees || [] } : empty);
    if (!clientId && !appt) api.get("/admin/clients").then((r) => setClients(r.data));
  }, [open, appt, initialDate]); // eslint-disable-line
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickClient = (id) => { const c = clients.find((x) => x.id === id); setF({ ...f, client_id: id, location: f.location || c?.address || "" }); };
  const [loading, save] = useSubmit(async () => {
    if (!f.client_id) throw { response: { data: { detail: "Seleziona il cliente" } } };
    if (!f.date) throw { response: { data: { detail: "Indica data e ora" } } };
    const body = { title: f.title, type: f.type, location: f.location, notes: f.notes, invitees: f.invitees, duration_min: Number(f.duration_min) || 60, date: new Date(f.date).toISOString() };
    return appt ? (await api.patch(`/appointments/${appt.id}`, body)).data : (await api.post("/appointments", { ...body, client_id: f.client_id })).data;
  }, (a) => { toast.success(appt ? "Appuntamento aggiornato: cliente e invitati avvisati" : "Appuntamento confermato e inviti spediti"); onSaved?.(a); onClose(); });
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} title={appt ? `Appuntamento con ${appt.client_name}` : "Nuovo appuntamento"} testid="appointment-dialog" wide>
      <form onSubmit={save} className="space-y-4">
        {!clientId && !appt && (
          <Field label="Cliente">
            <select value={f.client_id} onChange={(e) => pickClient(e.target.value)} className="rp-field" data-testid="appointment-client-select">
              <option value="">Seleziona un cliente…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.email}</option>)}
            </select>
          </Field>
        )}
        <div className="space-y-3"><p className="rp-label">Tipo</p>
          <div className="flex flex-wrap gap-2">{APPT_TYPES.map((t) => <Chip key={t.v} active={f.type === t.v} onClick={() => setF({ ...f, type: t.v })} data-testid={`appointment-type-${t.v}`}>{t.l}</Chip>)}</div>
        </div>
        <Field label="Oggetto"><input value={f.title} onChange={set("title")} className="rp-field" data-testid="appointment-title-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Data e ora"><input type="datetime-local" value={f.date} onChange={set("date")} className="rp-field" data-testid="appointment-date-input" /></Field>
          <Field label="Durata (min)"><input type="number" min={15} step={15} value={f.duration_min} onChange={set("duration_min")} className="rp-field" data-testid="appointment-duration-input" /></Field>
        </div>
        <Field label="Luogo"><AddressInput value={f.location} onChange={(v) => setF({ ...f, location: v })} placeholder="Corso Racconigi 6 bis, Torino" testid="appointment-location" /></Field>
        <Field label="Note"><textarea rows={2} value={f.notes} onChange={set("notes")} className="rp-field" data-testid="appointment-notes-input" /></Field>
        <InviteesPicker value={f.invitees} onChange={(v) => setF({ ...f, invitees: v })} />
        <Actions onCancel={onClose} loading={loading} label={appt ? "Salva modifiche" : "Crea appuntamento"} testid="appointment-submit" />
        {appt && <EditActions a={appt} onDone={() => { onSaved?.(); onClose(); }} />}
      </form>
    </Modal>
  );
};
